import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";

import { createBenchRoom, updateBenchRoom } from "../../../../api/courtApis";
import type { SelectOption } from "../../../../components/SearchableSelect";
import api from "../../../../lib/api";
import type {
  BenchRoomRow,
  BenchRow,
  HighCourtRow,
  JudgeOption,
} from "./types";
import {
  FormField,
  inputClassName,
  Modal,
  ModalActions,
  TextInput,
} from "./ModalShell";
import clsx from "clsx";
import SearchableSelect from "../../../../components/SearchableSelect";
import { useFormDirty } from "../../../../hooks/useFormDirty";

interface AddUpdateCourtHallProps {
  highCourt: HighCourtRow;
  bench: BenchRow;
  room?: BenchRoomRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const normalizeValue = (value?: string | number) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getEntityId = (value: unknown) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "_id" in value) {
    return String((value as { _id?: string })._id || "");
  }

  return "";
};

const JUDGE_PAGE_SIZE = 10;
const MIN_JUDGE_COUNT = 1;
const MAX_JUDGE_COUNT = 100;

const normalizeJudgeCountInput = (value: string) => {
  if (!value) return "";

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(MIN_JUDGE_COUNT);

  return String(
    Math.min(
      Math.max(Math.floor(numericValue), MIN_JUDGE_COUNT),
      MAX_JUDGE_COUNT,
    ),
  );
};

const clampJudgeCount = (value: string | number) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return MIN_JUDGE_COUNT;

  return Math.min(
    Math.max(Math.floor(numericValue), MIN_JUDGE_COUNT),
    MAX_JUDGE_COUNT,
  );
};

const mapJudgeOption = (judge: any): JudgeOption => ({
  value: String(judge.value || judge._id || ""),
  label:
    judge.label ||
    judge.fullName ||
    [judge.firstName, judge.lastName].filter(Boolean).join(" ") ||
    judge.name ||
    "Judge",
});

const mergeJudgeOptions = (
  currentOptions: JudgeOption[],
  nextOptions: JudgeOption[],
) => {
  const seen = new Set(currentOptions.map((option) => String(option.value)));
  const merged = [...currentOptions];

  nextOptions.forEach((option) => {
    const value = String(option.value);
    if (!value || seen.has(value)) return;

    seen.add(value);
    merged.push(option);
  });

  return merged;
};

export const AddUpdateCourtHall = ({
  highCourt,
  bench,
  room,
  onClose,
  onSaved,
}: AddUpdateCourtHallProps) => {
  const [judgePage, setJudgePage] = useState(1);
  const [judgeTotalPages, setJudgeTotalPages] = useState(1);
  const [judgeKeyword, setJudgeKeyword] = useState("");
  const [debouncedJudgeKeyword, setDebouncedJudgeKeyword] = useState("");
  const [judges, setJudges] = useState<JudgeOption[]>([]);
  const [isLoadingJudges, setIsLoadingJudges] = useState(false);
  const [isLoadingMoreJudges, setIsLoadingMoreJudges] = useState(false);

  const { data: benchRoomResponse } = useSWR(
    bench?._id ? `/api/court/get-bench-rooms/${bench._id}` : null,
  );

  const existingRooms = ((benchRoomResponse?.status === false
    ? []
    : benchRoomResponse?.data) || []) as BenchRoomRow[];
  const initialJudgeIds = useMemo(
    () =>
      (room?.judges || [])
        .map((judge: any) =>
          typeof judge === "string" ? judge : judge?._id || judge?.value,
        )
        .filter(Boolean)
        .map(String),
    [room?.judges],
  );
  const [form, setForm] = useState({
    benchName: room?.benchName || "",
    courtRoom: room?.courtRoom || "",
    floorNumber: String(room?.floorNumber || 1),
    judgesNumber: String(room?.judgesNumber || initialJudgeIds.length || 1),
    judges: initialJudgeIds.slice(
      0,
      Math.max(Number(room?.judgesNumber) || 1, initialJudgeIds.length || 1),
    ),
    status: room?.status || "active",
    facilities: room?.facilities || [],
  });
  const isPrimitiveDirty = useFormDirty(
    {
      benchName: room?.benchName || "",
      courtRoom: room?.courtRoom || "",
      floorNumber: String(room?.floorNumber || 1),
      judgesNumber: String(room?.judgesNumber || initialJudgeIds.length || 1),
      status: room?.status || "active",
    },
    {
      benchName: form.benchName,
      courtRoom: form.courtRoom,
      floorNumber: form.floorNumber,
      judgesNumber: form.judgesNumber,
      status: form.status,
    },
  );

  const isJudgesDirty = useMemo(() => {
    return (
      [...initialJudgeIds].sort().join(",") !==
      [...form.judges].sort().join(",")
    );
  }, [initialJudgeIds, form.judges]);

  const isFacilitiesDirty = useMemo(() => {
    const initial = [...(room?.facilities || [])].sort().join(",");
    const current = [...form.facilities].sort().join(",");
    return initial !== current;
  }, [room?.facilities, form.facilities]);

  const isDirty = isPrimitiveDirty || isJudgesDirty || isFacilitiesDirty;
  const [selectedJudgeOptions, setSelectedJudgeOptions] = useState<
    SelectOption[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const judgeCapacity = clampJudgeCount(form.judgesNumber);
  const hasMoreJudges = judgePage < judgeTotalPages;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedJudgeKeyword(judgeKeyword.trim());
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [judgeKeyword]);

  const fetchJudges = useCallback(
    async (nextPage: number, append = false) => {
      append ? setIsLoadingMoreJudges(true) : setIsLoadingJudges(true);

      try {
        const response = await api.get(
          `/api/bench/get-highcourt-judges?limit=${JUDGE_PAGE_SIZE}&page=${nextPage}&keyword=${encodeURIComponent(
            debouncedJudgeKeyword,
          )}`,
        );
        const payload = response?.data;

        if (!payload?.status) {
          if (!append) setJudges([]);
          setJudgePage(1);
          setJudgeTotalPages(1);
          return;
        }

        const nextJudges = ((payload.data || []) as any[])
          .map(mapJudgeOption)
          .filter((judge) => judge.value);

        setJudges((currentJudges) =>
          append ? mergeJudgeOptions(currentJudges, nextJudges) : nextJudges,
        );
        setJudgePage(nextPage);
        setJudgeTotalPages(Number(payload.pagination?.totalPages) || 1);
      } catch {
        if (!append) setJudges([]);
        setJudgePage(1);
        setJudgeTotalPages(1);
      } finally {
        append ? setIsLoadingMoreJudges(false) : setIsLoadingJudges(false);
      }
    },
    [debouncedJudgeKeyword],
  );

  useEffect(() => {
    fetchJudges(1, false);
  }, [fetchJudges]);

  const loadMoreJudges = () => {
    if (!hasMoreJudges || isLoadingJudges || isLoadingMoreJudges) return;
    fetchJudges(judgePage + 1, true);
  };

  useEffect(() => {
    if (!form.judges.length || selectedJudgeOptions.length) {
      return;
    }

    setSelectedJudgeOptions(
      form.judges.map((judgeId) => {
        const option = judges.find((judge) => String(judge.value) === judgeId);
        const roomJudge = (room?.judges || []).find((judge: any) => {
          const value = String(
            typeof judge === "string" ? judge : judge?._id || judge?.value,
          );

          return value === judgeId;
        }) as any;

        return {
          value: judgeId,
          label:
            option?.label ||
            roomJudge?.label ||
            roomJudge?.fullName ||
            [roomJudge?.firstName, roomJudge?.lastName]
              .filter(Boolean)
              .join(" ") ||
            roomJudge?.name ||
            "Assigned Judge",
        };
      }),
    );
  }, [form.judges, judges, room?.judges, selectedJudgeOptions.length]);

  const handleJudgeSelectionChange = (value: any) => {
    const nextSelection = Array.isArray(value) ? value : [];

    if (nextSelection.length > judgeCapacity) {
      toast.error(
        `You can select only ${judgeCapacity} judge${judgeCapacity > 1 ? "s" : ""}`,
      );
      return;
    }

    setSelectedJudgeOptions(nextSelection);
    setForm((prev) => ({
      ...prev,
      judges: nextSelection.map((item) => String(item.value)),
    }));
  };

  const handleSave = async () => {
    if (room && !isDirty) {
      toast.info("No changes detected");
      return;
    }
    const selectedJudgeIds = form.judges.filter(Boolean);
    if (!form.benchName || !form.courtRoom) {
      toast.error("Please fill all the mandatory fields");
      return;
    }
    const duplicateRoom = existingRooms.find((item) => {
      if (room?._id && item._id === room._id) return false;

      return (
        normalizeValue(item.benchName) === normalizeValue(form.benchName) ||
        normalizeValue(item.courtRoom) === normalizeValue(form.courtRoom)
      );
    });

    if (duplicateRoom) {
      const duplicateField =
        normalizeValue(duplicateRoom.benchName) ===
        normalizeValue(form.benchName)
          ? "Hall name"
          : "Room number";

      toast.error(`${duplicateField} already exists for this bench`);
      return;
    }

    try {
      setIsSaving(true);
      const parentBenchId = room
        ? getEntityId(room.bench) || bench._id
        : bench._id;
      const payload = {
        bench: parentBenchId,
        benchName: form.benchName,
        courtRoom: form.courtRoom,
        judgesNumber: clampJudgeCount(form.judgesNumber),
        judges: selectedJudgeIds,
        status: form.status,
        floorNumber: Number(form.floorNumber),
        facilities: form.facilities,
      };
      const res = room
        ? await updateBenchRoom({ _id: room._id, ...payload })
        : await createBenchRoom(payload);

      if (res?.data?.status) {
        toast.success(res.data.message || "Court hall saved");
        (res.data.reassignedJudges || []).forEach((item: any) => {
          toast.warning(
            `${item.judgeName || "Judge"} removed from ${item.courtHallName || "previous court hall"}`,
          );
        });
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save court hall");
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Failed to save court hall");
    } finally {
      setIsSaving(false);
    }
  };
  const getFloorLabel = (count: number) => {
    if (count === 0) return "Ground Floor";

    const suffixes = ["th", "st", "nd", "rd"];
    const value = count % 100;

    return `${count}${
      suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]
    } Floor`;
  };

  const handleFacilitySelectionChange = (value: any) => {
    // Remove the selected facility from the list
    if (form.facilities.includes(value)) {
      setForm((prev) => ({
        ...prev,
        facilities: prev.facilities.filter((item) => item !== value),
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      facilities: prev.facilities.concat(value),
    }));
  };

  return (
    <Modal
      title={room ? "Edit Court Hall" : "Add Court Hall"}
      subtitle={`${bench.benchName} - ${highCourt.highCourtName}`}
      onClose={onClose}
      scroll
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TextInput
          label="Hall Name"
          required
          value={form.benchName}
          onChange={(value) => setForm({ ...form, benchName: value })}
          placeholder="e.g. Chief Justice's Court"
        />
        <TextInput
          label="Room Number"
          required
          value={form.courtRoom}
          onChange={(value) => setForm({ ...form, courtRoom: value })}
          placeholder="e.g. Room 204"
        />
        <FormField label="Floor " required>
          <select
            value={form.floorNumber}
            onChange={(event) => {
              setForm({
                ...form,
                floorNumber: event.target.value,
              });
            }}
            className={inputClassName}
          >
            {[0, 1, 2, 3, 4, 5].map((count) => (
              <option key={count} value={count}>
                {getFloorLabel(count)}
              </option>
            ))}
          </select>
        </FormField>
        <TextInput
          label="Judge Count"
          required
          type="number"
          value={form.judgesNumber}
          onChange={(value) => {
            const nextValue = normalizeJudgeCountInput(value);
            const nextCapacity = clampJudgeCount(nextValue);
            const nextSelectedJudges = selectedJudgeOptions.slice(
              0,
              nextCapacity,
            );
            setSelectedJudgeOptions(nextSelectedJudges);
            setForm({
              ...form,
              judgesNumber: nextValue,
              judges: nextSelectedJudges.map((judge) => String(judge.value)),
            });
          }}
          placeholder="e.g. 3"
          inputClassCss="show-spinner"
          min={MIN_JUDGE_COUNT}
          max={MAX_JUDGE_COUNT}
        />
        <FormField label="Judges Assigned" className="md:col-span-2">
          <SearchableSelect
            options={judges}
            multiple
            value={selectedJudgeOptions}
            onChange={handleJudgeSelectionChange}
            onSearchChange={setJudgeKeyword}
            onLoadMore={loadMoreJudges}
            hasMore={hasMoreJudges}
            isLoadingMore={isLoadingMoreJudges}
            isLoadingOptions={isLoadingJudges}
            hideSelectedOptions
            placeholder="Select judges"
            searchPlaceholder="Search judge name..."
          />
          <p className="mt-2 text-xs text-slate-500">
            Selected {form.judges.length} / {judgeCapacity} judges
          </p>
        </FormField>
      </div>
      <div>
        <div className="mb-3 text-sm font-semibold text-slate-700">
          Facilities
        </div>
        <div className="flex flex-wrap gap-3">
          {[
            "Wi-Fi",
            "Video Conference",
            "Digital Recording",
            "Wheelchair Access",
            "Air Conditioning",
            "Projector",
          ].map((facility) => (
            <button
              type="button"
              onClick={() => handleFacilitySelectionChange(facility)}
              key={facility}
              className={clsx(
                "rounded-md border px-3 py-2 text-sm transition-colors",
                form?.facilities?.includes(facility)
                  ? "border-blue-600 bg-blue-50 text-blue-800"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300",
              )}
            >
              {facility}
            </button>
          ))}
        </div>
      </div>

      <FormField label="Status" required>
        <select
          value={form.status}
          onChange={(event) => setForm({ ...form, status: event.target.value })}
          className={inputClassName}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </FormField>
      <ModalActions
        label={room ? "Update Court Hall" : "Save Court Hall"}
        isSaving={isSaving}
        onCancel={onClose}
        onSave={handleSave}
        disabled={!!room && !isDirty}
      />
    </Modal>
  );
};
